import React, { useState, useEffect, useMemo } from "react";
import NavbarPag from "../components/NavbarPag";
import { useData } from "../contexts/DataContext";
import { useNavigate } from "react-router-dom";

interface Document {
  id: string;
  fileName: string;
  uploadDate: any;
}

const apiBase =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5001"
    : "https://api-kdps2abp6q-uc.a.run.app";

const toSeconds = (date: string) => new Date(date).getTime() / 1000;

const formatDate = (uploadDate: any) => {
  if (!uploadDate) return "—";
  const d = new Date(uploadDate);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("es-CL");
};

const ClientDocuments = () => {
  const { rutInit } = useData();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterName, setFilterName] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (rutInit === "") {
      navigate("/");
      return;
    }
    fetchDocuments();
  }, [rutInit]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/getDocuments?rut=${rutInit}`);
      const data = await res.json();
      setDocuments(data.documents || []);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (id: string) => {
    window.open(`${apiBase}/downloadDocument?id=${id}`, "_blank");
  };

  const filtered = useMemo(() => {
    return documents.filter((doc) => {
      const matchName =
        !filterName ||
        doc.fileName.toLowerCase().includes(filterName.toLowerCase());

      const sec = doc.uploadDate ? new Date(doc.uploadDate).getTime() / 1000 : 0;
      const matchFrom = !dateFrom || sec >= toSeconds(dateFrom);
      const matchTo = !dateTo || sec <= toSeconds(dateTo) + 86400; // incluir día completo

      return matchName && matchFrom && matchTo;
    });
  }, [documents, filterName, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const clearFilters = () => {
    setFilterName("");
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
  };

  const hasFilters = filterName || dateFrom || dateTo;

  if (rutInit === "") return <div></div>;

  return (
    <div className="w-screen min-h-screen bg-neutral-200 flex flex-col items-center">
      <NavbarPag />

      <div className="w-11/12 max-w-4xl mt-6 pb-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Mis Documentos</h1>
          <span className="text-sm text-gray-500 bg-white px-3 py-1 rounded shadow">
            RUT: {rutInit}
          </span>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Nombre de archivo
            </label>
            <input
              type="text"
              value={filterName}
              onChange={(e) => { setFilterName(e.target.value); setCurrentPage(1); }}
              placeholder="Buscar archivo..."
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div className="min-w-[140px]">
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Desde
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div className="min-w-[140px]">
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Hasta
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded text-sm"
            >
              Limpiar
            </button>
          )}
          <span className="text-xs text-gray-400 self-end pb-2">
            {filtered.length} documento{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <p className="text-center text-gray-500 py-12">Cargando...</p>
          ) : filtered.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b-2 border-gray-200">
                    <tr>
                      <th className="p-3 text-left text-sm font-semibold text-gray-600">
                        Archivo
                      </th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-600">
                        Fecha
                      </th>
                      <th className="p-3 text-center text-sm font-semibold text-gray-600">
                        Acción
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((doc) => (
                      <tr
                        key={doc.id}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td
                          className="p-3 text-sm max-w-xs truncate"
                          title={doc.fileName}
                        >
                          {doc.fileName}
                        </td>
                        <td className="p-3 text-sm text-gray-600">
                          {formatDate(doc.uploadDate)}
                        </td>
                        <td className="p-3 text-center">
                          <button
                            onClick={() => handleDownload(doc.id)}
                            className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-xs"
                          >
                            Descargar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-center items-center gap-3 p-4 border-t border-gray-100">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white px-4 py-2 rounded text-sm"
                >
                  ← Anterior
                </button>
                <span className="text-sm font-semibold text-gray-600">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white px-4 py-2 rounded text-sm"
                >
                  Siguiente →
                </button>
              </div>
            </>
          ) : (
            <p className="text-center text-gray-500 py-12">
              {documents.length === 0
                ? "No tienes documentos disponibles"
                : "No hay documentos que coincidan con los filtros"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientDocuments;
